import React, { useState, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  Typography,
  Box,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import ImageIcon from "@mui/icons-material/Image";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { GfFlyerItemDto } from "@swagger/typescript-fetch-goflyer";
import {
  ITEM_SELECTION_DIALOG_CONSTANTS,
  MODAL_CONSTANTS,
  VIEWER_COLORS,
  EDITOR_CONSTANTS,
} from "../constants";

interface ItemSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  allFlyerItems: GfFlyerItemDto[];
  currentPage: number; // 0-based page index
  deletedItemIds: Set<string>;
  itemsWithCoordinates: Set<string>;
  onSelectItem: (item: GfFlyerItemDto) => void;
  onCreateNew: () => void;
  onAIExtract: () => void;
}

/**
 * Dialog for selecting a flyer item to link with a coordinate box.
 *
 * This modal displays a searchable list of flyer items that are available for linking.
 * Available items are those without coordinates on any page, or items that have been
 * marked for deletion (unsaved). Users can search by item name, Chinese name, or category.
 * The dialog also provides an option to create a new flyer item if none of the existing
 * items are suitable.
 *
 * @param props - Component properties
 * @param props.open - Controls whether the dialog is visible
 * @param props.onClose - Callback invoked when the dialog should close
 * @param props.allFlyerItems - Complete list of all flyer items in the system
 * @param props.currentPage - Zero-based index of the current page being viewed
 * @param props.deletedItemIds - Set of item IDs that have been marked for deletion (unsaved)
 * @param props.itemsWithCoordinates - Set of item IDs that already have coordinates assigned
 * @param props.onSelectItem - Callback invoked when a user selects an item to link
 * @param props.onCreateNew - Callback invoked when user wants to create a new flyer item
 */
export const ItemSelectionDialog: React.FC<ItemSelectionDialogProps> = ({
  open,
  onClose,
  allFlyerItems,
  currentPage: _currentPage,
  deletedItemIds,
  itemsWithCoordinates: _itemsWithCoordinates,
  onSelectItem,
  onCreateNew,
  onAIExtract,
}) => {
  const [searchText, setSearchText] = useState("");

  const availableItems = useMemo((): GfFlyerItemDto[] => {
    return allFlyerItems.filter((item) => {
      // Exclude AI extraction temporary items
      if (
        item.id &&
        item.id.startsWith(EDITOR_CONSTANTS.AI_EXTRACT_TEMP_PREFIX)
      ) {
        return false;
      }

      if (!item.coordinate) {
        return true;
      }

      if (item.id && deletedItemIds.has(item.id)) {
        return true;
      }

      return false;
    });
  }, [allFlyerItems, deletedItemIds]);

  /**
   * Filters items based on search text, matching against name, Chinese name, and category.
   *
   * Performs a case-insensitive search across multiple item properties to help users
   * quickly find the item they're looking for. Returns all items if search text is empty.
   *
   * @param items - Array of flyer items to filter
   * @returns Filtered array containing only items matching the search criteria
   */
  const filterItems = useCallback(
    (items: GfFlyerItemDto[]) => {
      if (!searchText.trim()) return items;

      const searchLower = searchText.toLowerCase().trim();
      return items.filter((item) => {
        const nameMatch = item.name?.toLowerCase().includes(searchLower);
        const chineseMatch = item.nameChinese
          ?.toLowerCase()
          .includes(searchLower);
        const categoryMatch = item.gfFlyerItemCategory?.[0]?.name
          ?.toLowerCase()
          .includes(searchLower);

        return nameMatch || chineseMatch || categoryMatch;
      });
    },
    [searchText],
  );

  const filteredItems = useMemo(
    () => filterItems(availableItems),
    [filterItems, availableItems],
  );

  const handleSelectItem = (item: GfFlyerItemDto) => {
    onSelectItem(item);
    setSearchText("");
  };

  const handleCreateNew = () => {
    setSearchText("");
    onCreateNew();
  };

  const handleAIExtract = () => {
    setSearchText("");
    onAIExtract();
  };

  const handleClose = () => {
    setSearchText("");
    onClose();
  };

  /**
   * Retrieves the thumbnail image URL for a flyer item.
   *
   * Extracts the first image from the item's multi-image array to display as a thumbnail
   * in the selection list. Returns undefined if no images are available.
   *
   * @param item - The flyer item to get the thumbnail for
   * @returns The image source URL string, or undefined if no images exist
   */
  const getItemThumbnail = (item: GfFlyerItemDto): string | undefined => {
    return item.gfMultiImages?.[0]?.gfImage?.src;
  };

  /**
   * Renders a flyer item in the selection list with thumbnail and details.
   *
   * Creates a clickable list item displaying the item's thumbnail image, name (in both
   * English and Chinese if available), category, and deletion status. The item becomes
   * linked to the current page when clicked.
   *
   * @param item - The flyer item to render
   * @returns JSX element representing the list item
   */
  const renderItem = (item: GfFlyerItemDto) => {
    const thumbnail = getItemThumbnail(item);
    const isDeleted = item.id && deletedItemIds.has(item.id);

    return (
      <ListItem key={item.id} disablePadding>
        <ListItemButton
          onClick={() => handleSelectItem(item)}
          data-testid={`flyer-coordinate-item-${item.id}`}
        >
          <ListItemAvatar>
            <Avatar
              src={thumbnail}
              variant="rounded"
              sx={{
                width: ITEM_SELECTION_DIALOG_CONSTANTS.AVATAR_SIZE,
                height: ITEM_SELECTION_DIALOG_CONSTANTS.AVATAR_SIZE,
              }}
            >
              <ImageIcon />
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={
              <Box
                display="flex"
                alignItems="center"
                gap={ITEM_SELECTION_DIALOG_CONSTANTS.TITLE_GAP}
              >
                <Typography variant="body1" component="span">
                  {item.name || "Untitled"}
                </Typography>
                {item.nameChinese && (
                  <Typography
                    variant="body2"
                    component="span"
                    color="text.secondary"
                  >
                    ({item.nameChinese})
                  </Typography>
                )}
              </Box>
            }
            secondary={
              <Box
                display="flex"
                gap={ITEM_SELECTION_DIALOG_CONSTANTS.CHIP_GAP}
                mt={ITEM_SELECTION_DIALOG_CONSTANTS.CHIP_MARGIN_TOP}
                flexWrap="wrap"
              >
                {item.gfFlyerItemCategory?.[0] && (
                  <Chip
                    label={item.gfFlyerItemCategory[0].name}
                    size="small"
                    variant="outlined"
                    sx={{
                      height: MODAL_CONSTANTS.CHIP_HEIGHT,
                      fontSize: MODAL_CONSTANTS.CHIP_FONT_SIZE,
                    }}
                  />
                )}
                {isDeleted && (
                  <Chip
                    label="Deleted (Unsaved)"
                    size="small"
                    color="error"
                    sx={{
                      height: MODAL_CONSTANTS.CHIP_HEIGHT,
                      fontSize: MODAL_CONSTANTS.CHIP_FONT_SIZE,
                    }}
                  />
                )}
              </Box>
            }
            secondaryTypographyProps={{ component: "div" }}
          />
        </ListItemButton>
      </ListItem>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          height: MODAL_CONSTANTS.PAPER_HEIGHT,
          maxHeight: MODAL_CONSTANTS.PAPER_MAX_HEIGHT,
        },
      }}
      sx={{
        zIndex: MODAL_CONSTANTS.Z_INDEX,
      }}
      data-testid="flyer-coordinate-item-selection-dialog"
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            Link Coordinate Box to Flyer Item
          </Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleClose}
            aria-label="close"
            data-testid="flyer-coordinate-item-selection-close"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <TextField
          fullWidth
          placeholder="Search by name or category..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          variant="outlined"
          size="small"
          sx={{ mb: ITEM_SELECTION_DIALOG_CONSTANTS.SEARCH_MARGIN_BOTTOM }}
          autoFocus
          data-testid="flyer-coordinate-item-selection-search"
        />

        {filteredItems.length === 0 && searchText.trim() && (
          <Box
            textAlign="center"
            py={ITEM_SELECTION_DIALOG_CONSTANTS.RESULTS_PADDING_Y}
          >
            <Typography variant="body1" color="text.secondary">
              No items match your search
            </Typography>
          </Box>
        )}

        {filteredItems.length > 0 && (
          <Box mb={ITEM_SELECTION_DIALOG_CONSTANTS.SECTION_MARGIN_BOTTOM}>
            <Typography
              variant="subtitle2"
              color="primary"
              fontWeight="bold"
              mb={ITEM_SELECTION_DIALOG_CONSTANTS.SECTION_TITLE_MARGIN_BOTTOM}
            >
              Available Items ({filteredItems.length})
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              mb={ITEM_SELECTION_DIALOG_CONSTANTS.SECTION_CAPTION_MARGIN_BOTTOM}
              display="block"
            >
              These items don&apos;t have coordinates on any page
            </Typography>
            <List
              disablePadding
              dense
              data-testid="flyer-coordinate-item-selection-list"
            >
              {filteredItems.map((item) => renderItem(item))}
            </List>
          </Box>
        )}

        {filteredItems.length === 0 && !searchText.trim() && (
          <Box
            textAlign="center"
            py={ITEM_SELECTION_DIALOG_CONSTANTS.RESULTS_PADDING_Y}
          >
            <Typography
              variant="body1"
              color="text.secondary"
              mb={
                ITEM_SELECTION_DIALOG_CONSTANTS.EMPTY_STATE_TEXT_MARGIN_BOTTOM
              }
            >
              All items are already linked to pages
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create a new flyer item to add more coordinate boxes
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: ITEM_SELECTION_DIALOG_CONSTANTS.ACTIONS_PADDING,
          justifyContent: "space-between",
        }}
      >
        <Button
          variant="contained"
          startIcon={<AutoAwesomeIcon />}
          onClick={handleAIExtract}
          sx={{
            backgroundColor: VIEWER_COLORS.AI_EXTRACT_PURPLE,
            "&:hover": {
              backgroundColor: VIEWER_COLORS.AI_EXTRACT_PURPLE_HOVER,
            },
          }}
          data-testid="flyer-coordinate-btn-ai-extract"
        >
          AI Extract
        </Button>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateNew}
            sx={{
              backgroundColor: VIEWER_COLORS.PRIMARY_ORANGE,
              "&:hover": {
                backgroundColor: VIEWER_COLORS.PRIMARY_ORANGE_HOVER,
              },
            }}
            data-testid="flyer-coordinate-btn-create-new-item"
          >
            Create New Flyer Item
          </Button>
          <Button
            onClick={handleClose}
            variant="outlined"
            color="error"
            data-testid="flyer-coordinate-item-selection-cancel"
          >
            Cancel
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
